/**
 * Aktieklok — telt af naar middernacht. Hoort bij blocks/zorenza-reservation.liquid.
 *
 * Vervangt de persoonlijke vijf-minutenklok van Bouwbrief Z-09 §3 (besluit
 * Sjoerd, 6 okt 2026: te drukkend). Daarmee is bijna alles wat die versie
 * ingewikkeld maakte verdwenen — geen cart-attributen, geen laatste kans, geen
 * schrijven naar de mand bij elke paginaweergave, geen verouderde tabbladen.
 * De klok is voor iedereen dezelfde en hoeft nergens bewaard te worden.
 *
 * WELKE KLOK
 * Middernacht bij de BEZOEKER (besluit Sjoerd, 6 okt 2026). De Engelse teksten
 * worden gedeeld door zes markten — GB, US, AU, CA, IE en SG — en "until
 * midnight" moet kloppen voor wie het leest. Een winkeldeadline in Berlijnse
 * tijd loopt voor een Britse bezoeker om 23:00 af en voor een Amerikaanse
 * midden op de dag.
 *
 * Dat betekent dat de klok van de bezoeker leidend is. Zet iemand zijn
 * computer vooruit, dan ziet hij een andere stand — dat hoort bij deze keuze.
 * Liquid rekent nog wel een waarde uit voor de server-render (`data-rest`),
 * maar die is alleen voor bezoekers zonder JavaScript.
 *
 * Het doel wordt ÉÉN KEER bij het laden vastgelegd. Elke tik opnieuw de
 * eerstvolgende middernacht uitrekenen zou betekenen dat de teller om 00:00
 * vanzelf op 24:00:00 springt, en dat is precies de eeuwige klok die we niet
 * willen.
 *
 * BIJ NUL STOPT HIJ
 * De kaart verdwijnt en komt pas terug na een herlading. Niet doorrollen naar
 * de volgende middernacht: "gilt bis Mitternacht" is een belofte die een klant
 * kan nakijken, en een teller die voor zijn ogen opnieuw begint maakt die
 * onwaar.
 */

import { meld } from '@zorenza/q4-events';

const TAG = 'zorenza-reservation';

/** Ondergrens per stand, in seconden. Van ruim naar krap. */
const BANDEN = [
  { stand: 'm5', vanaf: 8 * 3600 },
  { stand: 'm4', vanaf: 6 * 3600 },
  { stand: 'm3', vanaf: 3 * 3600 },
  { stand: 'm2', vanaf: 1 * 3600 },
  { stand: 'm1', vanaf: 0 },
];

class ZorenzaReservation extends HTMLElement {
  /** prijs en vanaf-prijs in centen, per variant-id. */
  #varianten = new Map();
  #variantNu = '';

  /** Tijdstip van de eerstvolgende middernacht bij de bezoeker, in ms. */
  #doel = 0;

  #stand = '';
  #klokje = null;
  #verlopenGemeld = false;
  #form = null;
  #gemetenBreedte = 0;
  #maatWacht = 0;
  #waarnemer = null;

  connectedCallback() {
    this.#varianten = new Map(
      (this.dataset.varianten || '')
        .split(',')
        .filter(Boolean)
        .map((rij) => {
          const [id, prijs, vanaf] = rij.split(':');
          return [id, { prijs: Number(prijs) || 0, vanaf: Number(vanaf) || 0 }];
        })
    );
    this.#variantNu = this.dataset.variantNu || '';
    this.#doel = ZorenzaReservation.volgendeMiddernacht();
    this.#form =
      this.closest('product-component')?.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]');

    document.addEventListener('change', this.#onVariantWissel);
    document.addEventListener('visibilitychange', this.#onZichtbaar);
    this.#waarnemer = new ResizeObserver(this.#onMaat);
    this.#waarnemer.observe(this);

    if (!this.#toonbaar()) return;

    this.#meetHoogte();
    this.#tik();
    this.#startKlok();

    meld('countdown_shown', {
      handle: this.dataset.handle || '',
      markt: this.dataset.markt || '',
      resterend_sec: Math.round(this.#over()),
    });
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.#onVariantWissel);
    document.removeEventListener('visibilitychange', this.#onZichtbaar);
    this.#waarnemer?.disconnect();
    this.#waarnemer = null;
    clearTimeout(this.#maatWacht);
    this.#stopKlok();
  }

  /**
   * Geen korting, geen kaart. Elke tekst gaat over de Aktionspreis en over een
   * Rabatt; op een variant die gewoon zijn prijs heeft, is dat onzin.
   * Verbergen in plaats van weglaten, want bij een product waarvan de ene
   * variant wel en de andere niet afgeprijsd is, moet hij bij een wissel
   * kunnen verschijnen.
   */
  #toonbaar() {
    const v = this.#varianten.get(this.#variantNu);
    const korting = !!v && v.vanaf > v.prijs;
    this.hidden = !korting;
    return korting;
  }

  // ---- hoogte vastzetten --------------------------------------------------

  /**
   * De kaart mag de hoogte van het koopblok niet laten springen (§6). Een vast
   * getal haalt dat niet: in een andere taal en op een andere kolombreedte valt
   * de regelbreuk ergens anders. Daarom meten: alle standen één keer in het
   * echte element zetten en de hoogste als min-height vastleggen. Er wordt
   * tussendoor niet geschilderd, dus de bezoeker ziet niets.
   */
  #meetHoogte(opnieuw = false) {
    const kop = this.querySelector('.z-res__kop');
    const sub = this.querySelector('.z-res__sub');
    const klok = this.querySelector('.z-res__klok');
    if (!kop || !sub) return;

    // Meet nooit in een kolom die nog niet is uitgelijnd. Gebeurt dat toch, dan
    // wikkelt elke regel op een paar letters en komt er een min-height van rond
    // de 1900px uit die daarna blijft staan.
    const breedte = this.offsetWidth;
    if (breedte < 200) return;
    if (!opnieuw && breedte === this.#gemetenBreedte) return;
    this.#gemetenBreedte = breedte;

    const bewaarStand = this.dataset.stand;
    const bewaarKop = kop.textContent;
    const bewaarSub = sub.textContent;
    const bewaarKlok = klok ? klok.hidden : false;

    if (klok) klok.hidden = false;
    this.style.minHeight = '0px';

    let hoogste = 0;
    for (const { stand } of BANDEN) {
      this.dataset.stand = stand;
      kop.textContent = this.#vul(this.dataset[stand + 'Kop'] || '', 9, 59);
      sub.textContent = this.#vul(this.dataset[stand + 'Sub'] || '', 9, 59);
      hoogste = Math.max(hoogste, this.offsetHeight);
    }

    if (bewaarStand) this.dataset.stand = bewaarStand;
    kop.textContent = bewaarKop;
    sub.textContent = bewaarSub;
    if (klok) klok.hidden = bewaarKlok;

    this.style.minHeight = hoogste + 'px';

    if (this.offsetWidth !== breedte) {
      this.#gemetenBreedte = 0;
      this.#onMaat();
    }
  }

  #onMaat = () => {
    clearTimeout(this.#maatWacht);
    this.#maatWacht = setTimeout(() => {
      if (document.visibilityState === 'hidden') {
        this.#meetHoogte();
        return;
      }
      requestAnimationFrame(() => this.#meetHoogte());
    }, 200);
  };

  /** Terug op het tabblad: opnieuw meten en de klok bijtrekken. */
  #onZichtbaar = () => {
    if (document.visibilityState !== 'visible') return;
    this.#gemetenBreedte = 0;
    if (this.hidden) return;
    this.#meetHoogte();
    this.#tik();
  };

  // ---- klok ---------------------------------------------------------------

  #startKlok() {
    if (this.#klokje) return;
    this.#klokje = setInterval(() => this.#tik(), 1000);
  }

  #stopKlok() {
    if (!this.#klokje) return;
    clearInterval(this.#klokje);
    this.#klokje = null;
  }

  /**
   * De eerstvolgende middernacht bij de bezoeker. setHours met 24 rolt netjes
   * door naar de volgende dag, ook aan het eind van een maand of jaar, en
   * houdt rekening met de zomertijd van die zone.
   */
  static volgendeMiddernacht() {
    const d = new Date();
    d.setHours(24, 0, 0, 0);
    return d.getTime();
  }

  /** Seconden tot die middernacht, nu. */
  #over() {
    return (this.#doel - Date.now()) / 1000;
  }

  #tik() {
    const over = this.#over();

    if (over <= 0) {
      this.#stopKlok();
      this.hidden = true;
      if (!this.#verlopenGemeld) {
        this.#verlopenGemeld = true;
        meld('countdown_expired', {
          handle: this.dataset.handle || '',
          markt: this.dataset.markt || '',
        });
      }
      return;
    }

    const band = BANDEN.find((b) => over >= b.vanaf) || BANDEN[BANDEN.length - 1];
    this.#teken(band.stand, over);
  }

  // ---- tekenen ------------------------------------------------------------

  #teken(stand, over) {
    if (this.#stand !== stand) {
      this.#stand = stand;
      this.dataset.stand = stand;
      this.dataset.loopt = 'ja';

      const labels = {
        m5: this.dataset.labelVoor,
        m4: this.dataset.labelVoor,
        m3: this.dataset.labelAfloop,
        m2: this.dataset.labelAfloop,
        m1: this.dataset.labelLaatste,
      };
      const label = this.querySelector('.z-res__label-tekst');
      if (label) label.textContent = labels[stand] || '';
    }

    const uren = Math.floor(over / 3600);
    const minuten = Math.floor((over % 3600) / 60);

    const kop = this.querySelector('.z-res__kop');
    const sub = this.querySelector('.z-res__sub');
    const klok = this.querySelector('.z-res__klok');
    const vul = this.querySelector('.z-res__fill');

    if (kop) kop.textContent = this.#vul(this.dataset[stand + 'Kop'] || '', uren, minuten);
    if (sub) sub.textContent = this.#vul(this.dataset[stand + 'Sub'] || '', uren, minuten);
    if (klok) {
      klok.hidden = false;
      klok.textContent = this.#klokTekst(over);
    }
    // De balk loopt over de hele dag leeg, niet over de laatste minuten.
    if (vul) vul.style.width = Math.max(0, Math.min(1, over / 86400)) * 100 + '%';
  }

  #klokTekst(over) {
    const sec = Math.max(0, Math.ceil(over));
    const u = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    const mm = String(m).padStart(2, '0');
    const ss = String(s).padStart(2, '0');
    return u > 0 ? u + ':' + mm + ':' + ss : mm + ':' + ss;
  }

  /** Invoegvelden vervangen. Nooit een getypt bedrag — §5. */
  #vul(tekst, uren, minuten) {
    const v = this.#varianten.get(this.#variantNu) || { prijs: 0, vanaf: 0 };
    const korting = v.vanaf > v.prijs ? v.vanaf - v.prijs : 0;

    return tekst
      .replace(/\{\{\s*rabatt\s*\}\}/g, this.#geld(korting))
      .replace(/\{\{\s*aktionspreis\s*\}\}/g, this.#geld(v.prijs))
      .replace(/\{\{\s*normalpreis\s*\}\}/g, this.#geld(v.vanaf || v.prijs))
      .replace(/\{\{\s*stunden\s*\}\}/g, String(uren))
      .replace(/\{\{\s*minuten\s*\}\}/g, String(minuten));
  }

  // ---- variantwissel ------------------------------------------------------

  /**
   * De variantkiezer zet het verborgen id-veld niet in hetzelfde tikje als
   * waarin het change-event valt, dus even doorkijken tot het nummer verandert.
   */
  #onVariantWissel = (event) => {
    if (this.contains(event.target)) return;

    let pogingen = 0;
    const kijk = () => {
      const id = this.#form?.querySelector('input[name="id"]')?.value;
      if (id && id !== this.#variantNu) {
        this.#variantNu = id;
        this.#stand = '';

        if (!this.#toonbaar()) {
          this.#stopKlok();
          return;
        }

        // Andere bedragen zijn andere tekstlengtes, dus opnieuw meten.
        this.#meetHoogte(true);
        this.#tik();
        this.#startKlok();
        return;
      }
      if (++pogingen < 20) setTimeout(kijk, 100);
    };
    kijk();
  };

  /** Centen naar de opmaak van de winkel, via shop.money_format. */
  #geld(centen) {
    const patroon = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
    const bedrag = centen / 100;

    const knip = (decimalen, decimaalteken, duizendteken) => {
      const vast = bedrag.toFixed(decimalen);
      const deelstukken = vast.split('.');
      const metDuizend = deelstukken[0].replace(/\B(?=(\d{3})+(?!\d))/g, duizendteken);
      return decimalen > 0 ? metDuizend + decimaalteken + deelstukken[1] : metDuizend;
    };

    // Tags eruit: een valuta-app heeft het format in deze winkel gevuld met
    // <span class=cvc-money>…</span>. Dit gaat via textContent, dus zonder
    // strippen leest de klant die tags letterlijk.
    return patroon
      .replace('{{amount_no_decimals_with_comma_separator}}', knip(0, '', '.'))
      .replace('{{amount_with_comma_separator}}', knip(2, ',', '.'))
      .replace('{{amount_with_apostrophe_separator}}', knip(2, '.', "'"))
      .replace('{{amount_no_decimals}}', knip(0, '', ','))
      .replace('{{amount}}', knip(2, '.', ','))
      .replace(/<[^>]*>/g, '');
  }
}

if (!customElements.get(TAG)) customElements.define(TAG, ZorenzaReservation);
